import { Link } from 'react-router-dom'
import {
  Users,
  Calendar,
  Shield,
  ArrowRight,
  Bell,
  MapPin,
  Clock,
  FileText,
  Check,
  CheckCircle2,
  CircleAlert,
  Mic,
  Heart,
  Zap,
  type LucideIcon,
} from 'lucide-react'
import { ASILIFLOW_MONTHLY_PRICE_LABEL } from '@/config/billing'
import { ASILIFLOW_DEMO_CONTACT_PATH } from '@/config/contact'

// Feature Card Component
interface FeatureCardProps {
  icon: LucideIcon
  title: string
  description: string
}

function FeatureCard({ icon: Icon, title, description }: FeatureCardProps) {
  return (
    <div className="p-6 rounded-2xl border border-gray-100 dark:border-gray-800 hover:border-primary-100 dark:hover:border-primary-700 hover:shadow-soft transition-all duration-300 bg-white dark:bg-gray-900">
      <div className="w-12 h-12 bg-primary-50 dark:bg-primary-900/40 rounded-xl flex items-center justify-center mb-4">
        <Icon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">{title}</h3>
      <p className="text-gray-600 dark:text-gray-400">{description}</p>
    </div>
  )
}

// Process Card Component
interface ProcessCardProps {
  number: string
  title: string
  description: string
}

function ProcessCard({ number, title, description }: ProcessCardProps) {
  return (
    <div className="text-center">
      <div className="w-14 h-14 bg-primary-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-6">
        {number}
      </div>
      <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">{title}</h3>
      <p className="text-gray-600 dark:text-gray-400">{description}</p>
    </div>
  )
}

// Outcome Card Component — honest, pre-launch-friendly replacement for testimonials
interface OutcomeCardProps {
  icon: LucideIcon
  headline: string
  description: string
}

function OutcomeCard({ icon: Icon, headline, description }: OutcomeCardProps) {
  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl p-5 border border-gray-200 dark:border-gray-800 shadow-sm hover:border-primary-200 dark:hover:border-primary-700 hover:shadow-md transition-all">
      <div className="w-10 h-10 bg-primary-50 dark:bg-primary-900/40 rounded-lg flex items-center justify-center mb-4">
        <Icon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
      </div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2">
        {headline}
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
        {description}
      </p>
    </div>
  )
}

const features = [
  {
    icon: Calendar,
    title: 'Smart Scheduling',
    description: 'Every visit, open shift, and coverage gap tracked in real time. When the day changes, you see the dashboard instead of chasing texts.',
  },
  {
    icon: Mic,
    title: 'AI-Powered Shift Notes',
    description: 'Caregivers complete documentation on their phones. Voice-to-note workflows keep records current before the shift fades.',
  },
  {
    icon: Bell,
    title: 'Authorization Alerts',
    description: 'Authorizations, service windows, and renewal dates tracked automatically. No spreadsheet reminders. No 11pm panic calls.',
  },
  {
    icon: Zap,
    title: 'Open Shift Targeting',
    description: 'Send open shifts by position or hand-pick staff. SMS, email, and in-app notifications keep every response documented in one place.',
  },
  {
    icon: Heart,
    title: 'Family Portal',
    description: 'Families see their care team, schedules, and message staff directly. Feedback system with satisfaction ratings and reviews.',
  },
  {
    icon: Users,
    title: 'Skill & Position Matching',
    description: 'Match caregivers by skills, certifications, languages, and position. Priority-based routing ensures the best fit every time.',
  },
  {
    icon: MapPin,
    title: 'Geographic Routing',
    description: 'Optimize assignments by distance. See travel times, max travel preferences, and route caregivers efficiently.',
  },
  {
    icon: Shield,
    title: 'Audit-Ready Compliance',
    description: 'Every action is time-stamped with a clear audit trail. Incidents, exceptions, and follow-up stay visible instead of buried in folders.',
  },
  {
    icon: FileText,
    title: 'Plan of Care & Forms',
    description: 'Digital POC templates with PDF field mapping, e-signatures, and auto-populated shift tasks from active care plans.',
  },
  {
    icon: Clock,
    title: 'Operations-Ready Dashboards',
    description: 'One screen shows what is scheduled, what is pending review, and what is at risk. Export reports in seconds when answers are needed.',
  },
]

const stats = [
  { value: '80%', label: 'Less admin drag' },
  { value: '100%', label: 'Operational visibility' },
  { value: '24hr', label: 'Earlier risk signals' },
  { value: '30sec', label: 'Leadership answers' },
]

const beforeAsiliFlow = [
  'Schedules in one place',
  'Documentation somewhere else',
  'Authorizations tracked manually',
  'Compliance reviewed separately',
  'Managers constantly reacting instead of leading',
]

const afterAsiliFlow = [
  'One source of truth',
  'Real-time operational visibility',
  'Predictable daily execution',
  'Clear workforce accountability',
  'Leaders focused on growth instead of cleanup',
]

const steps = [
  {
    number: '1',
    title: 'Schedule. Assign. They Finish.',
    description: 'Caregivers see visit details and complete documentation from their phone while admins watch progress in real time.',
  },
  {
    number: '2',
    title: 'Review. Done.',
    description: 'Shift notes, incidents, and exceptions land in one queue. Approve, correct, or follow up before issues pile up.',
  },
  {
    number: '3',
    title: 'Operations Stay Current.',
    description: 'Authorizations renew on time. Coverage gaps surface early. You stop firefighting and start leading.',
  },
]

const outcomes = [
  {
    icon: Clock,
    headline: 'Operational visibility',
    description: 'See what is scheduled, pending, late, missing, and at risk without asking five people for updates.',
  },
  {
    icon: Users,
    headline: 'Workforce accountability',
    description: 'Track assignments, confirmations, availability, and follow-through in one operational record.',
  },
  {
    icon: Bell,
    headline: 'Authorization control',
    description: 'Keep renewal windows and service limits visible before they become revenue problems.',
  },
  {
    icon: FileText,
    headline: 'Documentation consistency',
    description: 'Keep visit records aligned with schedules, care plans, billing, and review workflows.',
  },
  {
    icon: Shield,
    headline: 'Compliance confidence',
    description: 'Stay audit-ready with clearer logs, visible exceptions, and fewer manual review gaps.',
  },
]

const fragmentedWorkflows = [
  'Spreadsheets',
  'Emails',
  'Paper processes',
  'Disconnected systems',
  'Manual follow-up',
]

const allFeatures = [
  'Smart Scheduling & Conflict Detection',
  'AI-Powered Voice Shift Notes',
  'Authorization Tracking & Tiered Alerts',
  'Open Shift Targeting by Position',
  'Family Portal with Messaging & Feedback',
  'Caregiver Skill & Position Matching',
  'Mobile-Friendly Field Experience',
  'Real-Time Data Across All Pages',
  'Plan of Care with E-Signatures',
  'Incident Reporting & Escalation',
  'Geographic Route Optimization',
  'Staff Availability & Time-Off Management',
  'Unlimited Clients & Caregivers',
  'Audit-Ready Compliance Reports',
  'SMS, Email & In-App Notifications',
]

const operationLeakItems = [
  {
    value: '10 admin hours',
    description: 'Rebuilding schedules, chasing confirmations, and reconciling changes by hand',
  },
  {
    value: '1 uncovered shift',
    description: 'A coordinator pulled into last-minute coverage instead of planned work',
  },
  {
    value: '1 delayed note',
    description: 'Billing held while documentation catches up after the visit',
  },
  {
    value: '1 missed authorization',
    description: 'Revenue at risk before the renewal or service limit is caught',
  },
]

export default function Landing() {
  return (
    <div className="overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative pt-20 pb-20 sm:pb-24 px-4 sm:px-6 lg:px-8">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-primary-50/50 to-white dark:from-primary-950/30 dark:to-gray-950 -z-10" />

        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-primary-50 dark:bg-primary-900/30 px-3 sm:px-4 py-2 rounded-full mb-8">
              <span className="w-2 h-2 bg-primary-500 rounded-full animate-pulse flex-shrink-0" />
              <span className="text-xs sm:text-sm font-medium text-primary-700 dark:text-primary-300 whitespace-nowrap">Healthcare Operations Platform</span>
            </div>

            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-gray-100 tracking-tight mb-6">
              Run Your{' '}
              <span className="text-primary-600 dark:text-primary-400">
                Healthcare Operations From One System
              </span>
            </h1>

            {/* Subheadline */}
            <div className="mb-8 max-w-2xl mx-auto space-y-4 text-xl text-gray-600 dark:text-gray-400">
              <p>
                Schedule visits, fill open shifts, track authorizations, review
                documentation, and manage daily operations from one dashboard.
              </p>
              <p>
                AsiliFlow replaces spreadsheets, disconnected software, and manual
                follow-up with one platform for the work that keeps care moving.
              </p>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
              <Link
                to={ASILIFLOW_DEMO_CONTACT_PATH}
                className="inline-flex items-center justify-center px-6 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
              >
                Book Demo
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <Link
                to="/signup"
                className="inline-flex min-h-[48px] items-center justify-center rounded-lg border-2 border-gray-700 dark:border-gray-500 bg-white dark:bg-gray-900 px-6 py-3 text-base font-medium text-gray-700 dark:text-gray-300 shadow-sm transition-colors hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Start Free Trial
              </Link>
            </div>

            {/* Trust badges */}
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Healthcare Operations Platform • Visibility • Accountability • Control
            </p>

            {/* Stats */}
            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <p className="text-3xl font-bold text-primary-600">{stat.value}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* Transformation Section */}
      <section id="transformation" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50 dark:bg-gray-950">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-6xl mx-auto mb-12">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-xs font-medium uppercase tracking-wide mb-4">
              <ArrowRight className="h-3.5 w-3.5" />
              Operational Control
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold leading-tight text-gray-900 dark:text-gray-100 mb-4">
              Stop Running Operations Across Multiple Systems
            </h2>
            <p className="mx-auto max-w-3xl text-lg text-gray-600 dark:text-gray-400">
              AsiliFlow brings healthcare operations into one source of truth so leaders
              stop reacting to problems and start managing with control.
            </p>
          </div>

          <div className="grid lg:grid-cols-[1fr_auto_1fr] gap-6 lg:gap-8 items-stretch">
            <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 sm:p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-11 w-11 rounded-xl bg-error-50 dark:bg-error-900/30 flex items-center justify-center">
                  <CircleAlert className="h-5 w-5 text-error-600 dark:text-error-300" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-error-600 dark:text-error-300">
                    Across Multiple Systems
                  </p>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                    Operations Before AsiliFlow
                  </h3>
                </div>
              </div>
              <div className="space-y-3">
                {beforeAsiliFlow.map((item) => (
                  <div key={item} className="flex items-start gap-3 rounded-xl bg-gray-50 dark:bg-gray-950 px-4 py-3">
                    <CircleAlert className="mt-0.5 h-4 w-4 flex-shrink-0 text-error-500" />
                    <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="hidden lg:flex items-center justify-center">
              <div className="h-14 w-14 rounded-full bg-primary-600 text-white shadow-lg shadow-primary-600/25 flex items-center justify-center">
                <ArrowRight className="h-6 w-6" />
              </div>
            </div>

            <div className="rounded-2xl border-2 border-primary-500 bg-white dark:bg-gray-900 p-6 sm:p-8 shadow-xl shadow-primary-600/10">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-11 w-11 rounded-xl bg-primary-50 dark:bg-primary-900/40 flex items-center justify-center">
                  <CheckCircle2 className="h-5 w-5 text-primary-600 dark:text-primary-300" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-primary-600 dark:text-primary-300">
                    One Source Of Truth
                  </p>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                    Operations After AsiliFlow
                  </h3>
                </div>
              </div>
              <div className="space-y-3">
                {afterAsiliFlow.map((item) => (
                  <div key={item} className="flex items-start gap-3 rounded-xl bg-primary-50 dark:bg-primary-950/40 px-4 py-3">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-600 dark:text-primary-300" />
                    <span className="text-sm sm:text-base font-medium text-gray-800 dark:text-gray-200">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dashboard Preview */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="relative max-w-5xl mx-auto">
            <div className="absolute -inset-4 bg-gradient-to-r from-primary-500/20 via-primary-600/10 to-primary-500/20 rounded-3xl blur-2xl"></div>
            <div className="relative bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-2xl overflow-hidden">
              <div className="bg-gray-100 dark:bg-gray-800 px-4 py-2 flex items-center gap-2 border-b border-gray-200 dark:border-gray-700">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-400"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400"></div>
                </div>
                <div className="flex-1 text-center">
                  <span className="text-xs text-gray-500 dark:text-gray-400">asiliflow.com/dashboard</span>
                </div>
              </div>
              <img
                src="/images/schedule-preview.png"
                alt="AsiliFlow healthcare operations dashboard with real-time visibility, accountability, and operational control"
                className="w-full h-auto"
                loading="lazy"
              />
            </div>
          </div>
          <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-6">
            One operational dashboard for visibility, accountability, authorization control, documentation consistency, and compliance confidence
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-4 sm:px-6 lg:px-8 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-16 items-center mb-20">
            <div>
              <span className="text-sm font-semibold uppercase tracking-wide text-primary-600 dark:text-primary-400">
                Core Value
              </span>
              <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100">
                Stop Running Your Organization In Fragments
              </h2>
              <p className="mt-5 text-lg text-gray-600 dark:text-gray-400">
                AsiliFlow centralizes the operational backbone of your organization
                so your team can execute with confidence and leadership can make
                decisions with clarity.
              </p>
            </div>

            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Most healthcare organizations still operate through:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {fragmentedWorkflows.map((item) => (
                  <div
                    key={item}
                    className="flex items-center gap-3 rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 px-4 py-3"
                  >
                    <CircleAlert className="h-4 w-4 flex-shrink-0 text-error-500" />
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="text-center mb-12">
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              One platform for the workflows that control the day
            </h3>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Scheduling, documentation, authorizations, compliance, and workforce
              visibility finally move together.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature) => (
              <FeatureCard
                key={feature.title}
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
              />
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-24 px-4 sm:px-6 lg:px-8 bg-gray-50 dark:bg-gray-950">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              Three Steps. No Email Chains.
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Caregivers complete visits on their phones. You review in one queue.
              Operations stay current without chasing updates across five places.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step) => (
              <ProcessCard
                key={step.number}
                number={step.number}
                title={step.title}
                description={step.description}
              />
            ))}
          </div>
        </div>
      </section>


      {/* Pricing Section */}
      <section id="pricing" className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-50 dark:bg-primary-900/30 border border-primary-100 dark:border-primary-800 mb-4">
              <Clock className="w-3 h-3 flex-shrink-0 text-primary-600" />
              <span className="text-xs font-medium text-center text-primary-600 dark:text-primary-400">
                Lock in {ASILIFLOW_MONTHLY_PRICE_LABEL}/mo before rates adjust
              </span>
            </div>
            <h2 className="mx-auto max-w-4xl px-4 text-2xl sm:text-3xl md:text-4xl font-semibold leading-tight text-gray-900 dark:text-gray-100 mb-4">
              Stop Paying More Every Time You Hire Someone.
            </h2>
            <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto px-4">
              Per-seat pricing penalizes growth. Whether you're coordinating 10 caregivers
              or 1,000 visits a month, AsiliFlow stays the same price.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="relative">
              <div className="absolute -inset-1 rounded-3xl opacity-20 blur-xl bg-primary-500" />

              <div className="relative bg-white dark:bg-gray-900 rounded-3xl border-2 border-primary-500 shadow-2xl overflow-hidden">
                <div className="text-center py-3 text-white text-sm font-medium bg-primary-600">
                  Full Platform • 30-Day Trial • Cancel Anytime
                </div>

                <div className="p-8 sm:p-12">
                  <div className="text-center mb-8">
                    <div className="mb-4">
                      <div className="flex items-baseline justify-center gap-2 mb-2 flex-wrap">
                        <span className="text-xl sm:text-2xl text-gray-400 dark:text-gray-500 line-through">$499</span>
                        <span className="text-5xl sm:text-6xl md:text-7xl font-bold text-gray-900 dark:text-gray-100">
                          {ASILIFLOW_MONTHLY_PRICE_LABEL}
                        </span>
                        <span className="text-xl sm:text-2xl text-gray-600 dark:text-gray-400">/month</span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 px-4">
                        Flat rate. No contracts. No per-seat multiplication.
                      </p>
                    </div>

                    <div className="flex flex-wrap justify-center gap-4 mb-8">
                      <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-50 dark:bg-primary-900/30 border border-primary-100 dark:border-primary-800">
                        <Users className="w-4 h-4 text-primary-600" />
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          Unlimited Staff
                        </span>
                      </div>
                      <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-50 dark:bg-primary-900/30 border border-primary-100 dark:border-primary-800">
                        <FileText className="w-4 h-4 text-primary-600" />
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          Unlimited Clients
                        </span>
                      </div>
                      <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-50 dark:bg-primary-900/30 border border-primary-100 dark:border-primary-800">
                        <Shield className="w-4 h-4 text-primary-600" />
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          CMS Compliance
                        </span>
                      </div>
                    </div>

                    <div className="space-y-3 mb-8">
                      <Link
                        to="/signup"
                        className="block w-full px-8 py-4 rounded-xl text-base font-semibold text-white bg-primary-600 hover:bg-primary-700 transition shadow-lg"
                      >
                        Lock In {ASILIFLOW_MONTHLY_PRICE_LABEL}/Month
                      </Link>
                      <Link
                        to={ASILIFLOW_DEMO_CONTACT_PATH}
                        className="block w-full px-8 py-4 rounded-xl border-2 border-gray-200 dark:border-gray-700 text-base font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition"
                      >
                        See a 10-Minute Demo
                      </Link>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        No credit card • Full access • One-click cancellation
                      </p>
                    </div>
                  </div>

                  {/* Features grid */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
                    <h3 className="text-center text-lg font-semibold text-gray-900 dark:text-gray-100 mb-6">
                      Everything Included. No Hidden Tiers.
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                      {allFeatures.map((feature, i) => (
                        <div key={i} className="flex items-start gap-3">
                          <Check className="w-5 h-5 mt-0.5 flex-shrink-0 text-primary-600" />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* ROI */}
                  <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
                    <div className="text-center">
                      <p className="text-sm font-semibold text-primary-600 dark:text-primary-400 mb-3">
                        Where the money leaks
                      </p>
                      <h3 className="mx-auto max-w-3xl text-xl sm:text-2xl font-semibold leading-tight text-gray-900 dark:text-gray-100 mb-3">
                        Operational costs show up before the missed visit.
                      </h3>
                      <p className="mx-auto max-w-2xl text-sm sm:text-base text-gray-600 dark:text-gray-400">
                        The real cost is admin time, uncovered shifts, delayed notes,
                        and authorizations that should have been caught earlier.
                      </p>
                    </div>

                    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
                      {operationLeakItems.map((item) => (
                        <div
                          key={item.value}
                          className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 text-left"
                        >
                          <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                            {item.value}
                          </p>
                          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                            {item.description}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Trust badges */}
          <div className="mt-12 text-center px-4">
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 flex-shrink-0 text-primary-600" />
                <span>HIPAA-Compliant Infrastructure</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 flex-shrink-0 text-primary-600" />
                <span>Enterprise-Grade Security</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 flex-shrink-0 text-primary-600" />
                <span>SOC 2 Certified Partners</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 flex-shrink-0 text-primary-600" />
                <span>99.9% Uptime Monitored</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Outcomes — What You'll Gain */}
      <section id="faq" className="py-16 sm:py-24 border-t border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 px-4">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-50 dark:bg-primary-950/40 text-primary-700 dark:text-primary-300 text-xs font-medium uppercase tracking-wide mb-4">
              <Zap className="h-3.5 w-3.5" />
              Outcomes
            </span>
            <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
              What Changes After AsiliFlow?
            </h2>
            <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 max-w-4xl mx-auto">
              You gain the control needed to stop reacting to problems and start managing proactively.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 px-4">
            {outcomes.map((outcome, i) => (
              <OutcomeCard
                key={i}
                icon={outcome.icon}
                headline={outcome.headline}
                description={outcome.description}
              />
            ))}
          </div>

          <p className="mt-10 text-center text-lg font-medium text-gray-900 dark:text-gray-100">
            Most importantly: your leaders focus on growth instead of cleanup.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-24 bg-primary-50 dark:bg-primary-950/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-[2.5rem] font-semibold leading-tight text-gray-900 dark:text-gray-100 mb-4">
            Stop Reacting To Problems. Start Managing Proactively.
          </h2>
          <p className="text-base sm:text-lg text-gray-700 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
            When a shift changes, an authorization is at risk, or documentation is still pending,
            your team should know immediately and know exactly what to do next.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to={ASILIFLOW_DEMO_CONTACT_PATH}
              className="w-full sm:w-auto px-8 py-3 rounded-xl text-sm sm:text-base font-medium text-white bg-primary-600 hover:bg-primary-700 transition inline-flex items-center justify-center gap-2 shadow-lg min-h-[48px]"
            >
              Book Demo
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/signup"
              className="w-full sm:w-auto px-8 py-3 rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm sm:text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition min-h-[48px]"
            >
              Start Free Trial
            </Link>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-6">
            No card required • 30-day access • Walk away anytime
          </p>
        </div>
      </section>
    </div>
  )
}
